import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import { Flex, xcss } from '@atlaskit/primitives';

import { LegacyContentIcon } from '@confluence/icons/entry-points/LegacyContentIcon';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title';
import {
	getEmojiDraftFromProperties,
	getEmojiPublishedFromProperties,
} from '@confluence/emoji-title/entry-points/transformer';
import {
	getEmojiFromTitle,
	removeEmojiFromTitle,
} from '@confluence/emoji-title/entry-points/helpers';
import { isValidEmojiOrFavicon } from '@confluence/emoji-title/entry-points/isValidEmojiOrFavicon';
import { LiveDocsIconTile } from '@confluence/live-pages-features/entry-points/LiveDocsIconTile';
import { fg } from '@confluence/feature-gating';
import { useIsNav4Enabled } from '@confluence/nav4-enabled';

const i18n = defineMessages({
	liveDoc: {
		id: 'entity-view.live.page.label',
		defaultMessage: 'Live doc',
		description: 'Label for live page icon. Denotes this content is a live page.',
	},
	page: {
		id: 'entity-view.page.label',
		defaultMessage: 'Page',
		description: 'Label for page icon. Denotes this content is a page.',
	},
	blogpost: {
		id: 'entity-view.blog.label',
		defaultMessage: 'Blog',
		description: 'Label for blog icon. Denotes this content is a blog.',
	},
	whiteboard: {
		id: 'entity-view.whiteboard.label',
		defaultMessage: 'Whiteboard',
		description: 'Label for whiteboard icon. Denotes this content is a whiteboard.',
	},
	database: {
		id: 'entity-view.database.label',
		defaultMessage: 'Database',
		description: 'Label for database icon. Denotes this content is a database.',
	},
	folder: {
		id: 'entity-view.folder.label',
		defaultMessage: 'Folder',
		description: 'Label for folder icon. Denotes this content is a folder.',
	},
	generic: {
		id: 'entity-view.generic.label',
		defaultMessage: 'Content icon',
		description: 'Label for generic content when the type cannot be determined.',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiWrapper = styled.div<{ removeMargin?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: ({ removeMargin }) => (removeMargin ? token('space.0') : token('space.negative.075')),
	width: '36px',
});

const iconBorderStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderRadius: '4px',
	borderWidth: 'border.width',
	height: '32px',
	width: '32px',
});

export type ContentIconProps = {
	contentType: string;
	removeMargin: boolean;
	emoji?: string;
	subType?: string | null;
};

export const EmojiContentIcon: FC<ContentIconProps> = ({
	contentType,
	removeMargin,
	emoji,
	subType,
}) => {
	const isNav4Enabled = useIsNav4Enabled();
	const intl = useIntl();
	const isLivePage = contentType === 'page' && subType === 'live';

	if (isNav4Enabled) {
		return (
			<Flex alignItems="center" justifyContent="center" xcss={iconBorderStyles}>
				<ContentIcon type={contentType} subType={subType} emoji={emoji} />
			</Flex>
		);
	}

	if (isValidEmojiOrFavicon(emoji, contentType)) {
		return (
			<EmojiWrapper data-testid="emoji" removeMargin={removeMargin}>
				<LazyEmojiComponentLoader
					emoji={emoji || ''}
					contentType={contentType}
					context="entityView"
					height={24}
				/>
			</EmojiWrapper>
		);
	}

	return (
		<div data-testid="content-icon" style={{ marginRight: removeMargin ? '0' : '6px' }}>
			{isLivePage && fg('confluence_live_pages_open_beta_trait_opted_in') ? (
				<LiveDocsIconTile
					label={intl.formatMessage(i18n.liveDoc)}
					size="medium"
					testId="live-icon-tile"
				/>
			) : (
				<LegacyContentIcon
					type={contentType}
					label={intl.formatMessage(i18n[contentType] || i18n.generic)}
					size="medium"
				/>
			)}
		</div>
	);
};

export const getEmoji = (content) => {
	const emojiPropertyDraft = getEmojiDraftFromProperties(content);
	const emojiPropertyPublished = getEmojiPublishedFromProperties(content);
	const emojiProperty = content.status === 'draft' ? emojiPropertyDraft : emojiPropertyPublished;

	const emoji = emojiProperty || getEmojiFromTitle(content.title);

	return {
		hasEmojiProperty: Boolean(emojiProperty),
		emoji,
	};
};

export const getTitle = (originalTitle, hasEmojiProperty) =>
	hasEmojiProperty ? originalTitle : removeEmojiFromTitle(originalTitle);
